import { Loader2 } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import type {
  RohsComponentComplianceFileDetailView,
  RohsTestReportAuditStoredEntry,
  RohsTestReportAuditUploadedByActor,
} from '@/shared/hooks/api/rohsComplianceFiles'
import {
  useRunCatalogRohsComponentTestReportAudit,
  useRunRohsComponentTestReportAudit,
} from '@/shared/hooks/api/rohsComplianceFiles'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetFooter,
  SheetHeader,
  SheetTitle,
} from '@/components/ui/sheet'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import {
  auditReasoning,
  formatAuditTimestamp,
  verdictBadgeVariant,
} from './rohsTestReportAuditDisplay'

type V2ComponentRohsTestReportAuditSheetProps = {
  open: boolean
  onOpenChange: (open: boolean) => void
  componentId: string
  file: RohsComponentComplianceFileDetailView
  canWrite: boolean
  auditInProgress: boolean
  scope: 'catalog' | 'product'
  productId?: string
}

const BASE_I18N_KEY = 'v2.pages.components.detail.rohsBriefing.testReportAudit'

function uploadedByActorLabel(
  translate: ReturnType<typeof useTranslation>['t'],
  actor: RohsTestReportAuditUploadedByActor | null
): string {
  if (actor == null) {
    return ''
  }
  return translate(`${BASE_I18N_KEY}.uploadedByActor.${actor}`)
}

function RohsTestReportAuditLatestDetails({
  entry,
  locale,
  translate,
}: {
  entry: RohsTestReportAuditStoredEntry
  locale: string
  translate: ReturnType<typeof useTranslation>['t']
}) {
  const hasMissingSubstances = entry.missingSubstanceCodes.length > 0
  const hasOverLimitMaterials = entry.overLimitMaterials.length > 0

  return (
    <div className='space-y-3'>
      <div className='flex flex-wrap items-center gap-2'>
        <Badge variant={verdictBadgeVariant(entry.verdict)}>
          {translate(`${BASE_I18N_KEY}.verdicts.${entry.verdict}`)}
        </Badge>
        <span className='text-xs text-muted-foreground'>
          {formatAuditTimestamp(entry.timestamp, locale)}
        </span>
      </div>
      <p className='text-sm text-muted-foreground'>
        {translate(`${BASE_I18N_KEY}.trigger.${entry.trigger}`, {
          actor: uploadedByActorLabel(translate, entry.uploadedByActor),
        })}
      </p>
      <p className='text-sm'>{auditReasoning(entry, locale)}</p>
      {hasMissingSubstances ? (
        <p className='text-sm text-muted-foreground'>
          {translate(`${BASE_I18N_KEY}.missingSubstances`, {
            codes: entry.missingSubstanceCodes.join(', '),
          })}
        </p>
      ) : null}
      {hasOverLimitMaterials ? (
        <ul className='list-inside list-disc text-sm text-muted-foreground'>
          {entry.overLimitMaterials.map((row) => {
            let materialLabel = '—'
            if (row.materialName != null) {
              materialLabel = row.materialName
            }
            return (
              <li key={`${row.substanceCode}-${materialLabel}`}>
                {translate(`${BASE_I18N_KEY}.overLimitRow`, {
                  substance: row.substanceCode,
                  material: materialLabel,
                  value: row.valueMgKg,
                  limit: row.limitMgKg,
                })}
              </li>
            )
          })}
        </ul>
      ) : null}
    </div>
  )
}

function RunAuditSheetButtonLabel({
  isRunning,
  label,
}: {
  isRunning: boolean
  label: string
}) {
  if (isRunning) {
    return (
      <>
        <Loader2 className='mr-2 size-4 animate-spin' />
        {label}
      </>
    )
  }
  return label
}

function RohsTestReportAuditHistoryTable({
  history,
  locale,
  translate,
}: {
  history: RohsTestReportAuditStoredEntry[]
  locale: string
  translate: ReturnType<typeof useTranslation>['t']
}) {
  return (
    <div className='space-y-2'>
      <p className='text-xs font-medium text-muted-foreground'>
        {translate(`${BASE_I18N_KEY}.historyTitle`)}
      </p>
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>{translate(`${BASE_I18N_KEY}.historyColumns.when`)}</TableHead>
            <TableHead>
              {translate(`${BASE_I18N_KEY}.historyColumns.verdict`)}
            </TableHead>
            <TableHead>
              {translate(`${BASE_I18N_KEY}.historyColumns.trigger`)}
            </TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {history.map((entry) => (
            <TableRow key={entry.timestamp}>
              <TableCell className='text-xs'>
                {formatAuditTimestamp(entry.timestamp, locale)}
              </TableCell>
              <TableCell>
                <Badge variant={verdictBadgeVariant(entry.verdict)}>
                  {translate(`${BASE_I18N_KEY}.verdicts.${entry.verdict}`)}
                </Badge>
              </TableCell>
              <TableCell className='text-xs text-muted-foreground'>
                {translate(`${BASE_I18N_KEY}.trigger.${entry.trigger}`, {
                  actor: uploadedByActorLabel(translate, entry.uploadedByActor),
                })}
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </div>
  )
}

export function V2ComponentRohsTestReportAuditSheet({
  open,
  onOpenChange,
  componentId,
  file,
  canWrite,
  auditInProgress,
  scope,
  productId,
}: V2ComponentRohsTestReportAuditSheetProps) {
  const { t, i18n } = useTranslation('common')
  const runCatalog = useRunCatalogRohsComponentTestReportAudit(componentId)
  const runProduct = useRunRohsComponentTestReportAudit(
    productId ?? '',
    componentId
  )

  const isCatalogScope = scope === 'catalog'
  let runMutation = runProduct
  if (isCatalogScope) {
    runMutation = runCatalog
  }

  const latestEntry = file.latestTestReportAudit ?? null
  const history = file.testReportAuditHistory ?? []
  const isRunning = auditInProgress || runMutation.isPending
  const hasLatestAudit = latestEntry != null
  const showEmptyState = !hasLatestAudit && !isRunning
  const hasHistory = history.length > 0
  const showRunAuditFooter = canWrite

  async function handleRunAudit() {
    await runMutation.mutateAsync()
  }

  let runAuditButtonContent = t(`${BASE_I18N_KEY}.run`)
  if (isRunning) {
    runAuditButtonContent = t(`${BASE_I18N_KEY}.running`)
  }

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent className='flex w-full flex-col sm:max-w-lg'>
        <SheetHeader>
          <SheetTitle>{t(`${BASE_I18N_KEY}.title`)}</SheetTitle>
          <SheetDescription>{t(`${BASE_I18N_KEY}.hint`)}</SheetDescription>
        </SheetHeader>

        <div className='min-h-0 flex-1 space-y-4 overflow-y-auto px-4 pb-4'>
          {hasLatestAudit && latestEntry != null ? (
            <RohsTestReportAuditLatestDetails
              entry={latestEntry}
              locale={i18n.language}
              translate={t}
            />
          ) : null}
          {showEmptyState ? (
            <p className='text-sm text-muted-foreground'>
              {t(`${BASE_I18N_KEY}.empty`)}
            </p>
          ) : null}
          {hasHistory ? (
            <RohsTestReportAuditHistoryTable
              history={history}
              locale={i18n.language}
              translate={t}
            />
          ) : null}
        </div>

        {showRunAuditFooter ? (
          <SheetFooter>
            <Button
              type='button'
              disabled={isRunning}
              onClick={() => {
                void handleRunAudit()
              }}
            >
              <RunAuditSheetButtonLabel
                isRunning={isRunning}
                label={runAuditButtonContent}
              />
            </Button>
          </SheetFooter>
        ) : null}
      </SheetContent>
    </Sheet>
  )
}
